<template>
	<div class="chat-bubble-row" :class="`chat-bubble-row--${role}`">
		<div v-if="role === 'assistant'" class="chat-bubble-avatar">
			<GuIAreGlyph :size="22" />
		</div>

		<div
			class="chat-bubble"
			:class="[
				`chat-bubble--${role}`,
				{ 'chat-bubble--pending': pending && !content }
			]"
			:style="role === 'user' ? userBubbleStyle : null"
		>
			<div
				v-if="content && role === 'assistant'"
				class="chat-bubble-md"
				v-html="renderedContent"
			></div>
			<span v-else-if="content" class="chat-bubble-text">{{ content }}</span>
			<span v-else-if="pending" class="chat-bubble-dots">
				<i></i><i></i><i></i>
			</span>
		</div>
	</div>
</template>

<script>
import GuIAreGlyph from './GuIAreGlyph.vue';
import { renderMarkdown } from '@/utils/markdown';
import { chatActions } from '@/store/chatStore';

export default {
	name: 'ChatBubble',

	components: { GuIAreGlyph },

	props: {
		role: {
			type: String,
			required: true, // 'user' | 'assistant'
		},
		content: {
			type: String,
			default: '',
		},
		pending: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		userBubbleStyle() {
			return {
				background: this.$vuetify?.theme?.themes?.light?.primary,
				color: this.$corTextoPrimary,
			};
		},

		renderedContent() {
			return renderMarkdown(this.content);
		},
	},

	mounted() {
		this.$el.addEventListener('click', this.onLinkClick);
	},

	beforeDestroy() {
		this.$el.removeEventListener('click', this.onLinkClick);
	},

	methods: {
		/**
		 * Intercepta cliques em links internos do markdown.
		 * Internos (começam com /) → Vue Router + minimiza o drawer.
		 * Externos → comportamento padrão do navegador.
		 */
		onLinkClick(event) {
			const a = event.target.closest('a[href]');
			if (!a) return;

			const href = a.getAttribute('href');
			if (!href || !href.startsWith('/')) return;

			event.preventDefault();
			chatActions.minimize();

			if (this.$router.currentRoute.fullPath !== href) {
				this.$router.push(href).catch(() => {});
			}
		},
	},
};
</script>

<style scoped>
.chat-bubble-row {
	display: flex;
	align-items: flex-end;
	gap: 8px;
	margin: 6px 0;
	animation: bubble-in 220ms ease both;
}

.chat-bubble-row--user {
	justify-content: flex-end;
}

.chat-bubble-row--assistant {
	justify-content: flex-start;
}

.chat-bubble-avatar {
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.chat-bubble {
	max-width: 86%;
	padding: 10px 14px;
	border-radius: 16px;
	font-size: 14px;
	line-height: 1.55;
	word-break: break-word;
}

.chat-bubble-text {
	white-space: pre-wrap;
}

.chat-bubble-md {
	/* o markdown já vem com <p>/<br>; não duplica quebras */
	white-space: normal;
}

.chat-bubble-md ::v-deep > *:first-child {
	margin-top: 0;
}

.chat-bubble-md ::v-deep > *:last-child {
	margin-bottom: 0;
}

.chat-bubble-md ::v-deep p {
	margin: 0 0 8px 0;
}

.chat-bubble-md ::v-deep strong,
.chat-bubble-md ::v-deep b {
	font-weight: 700;
	color: #0f172a;
}

.chat-bubble-md ::v-deep em,
.chat-bubble-md ::v-deep i {
	font-style: italic;
}

.chat-bubble-md ::v-deep ul,
.chat-bubble-md ::v-deep ol {
	margin: 4px 0 8px 0;
	padding-left: 22px;
}

.chat-bubble-md ::v-deep li {
	margin: 2px 0;
}

.chat-bubble-md ::v-deep li > p {
	margin: 0;
}

.chat-bubble-md ::v-deep h1,
.chat-bubble-md ::v-deep h2,
.chat-bubble-md ::v-deep h3,
.chat-bubble-md ::v-deep h4 {
	font-size: 14px;
	font-weight: 700;
	margin: 10px 0 4px 0;
	color: #0f172a;
}

.chat-bubble-md ::v-deep blockquote {
	margin: 6px 0;
	padding: 4px 10px;
	border-left: 3px solid rgba(99, 102, 241, 0.5);
	background: rgba(99, 102, 241, 0.06);
	border-radius: 4px;
	color: #334155;
}

.chat-bubble-md ::v-deep code {
	background: rgba(15, 23, 42, 0.07);
	padding: 1px 5px;
	border-radius: 4px;
	font-family: 'JetBrains Mono', 'Menlo', monospace;
	font-size: 12.5px;
}

.chat-bubble-md ::v-deep pre {
	background: rgba(15, 23, 42, 0.05);
	border: 1px solid rgba(148, 163, 184, 0.3);
	border-radius: 8px;
	padding: 8px 10px;
	overflow-x: auto;
	margin: 6px 0;
}

.chat-bubble-md ::v-deep pre code {
	background: transparent;
	padding: 0;
}

.chat-bubble-md ::v-deep a {
	color: #4338ca;
	text-decoration: underline;
}

.chat-bubble-md ::v-deep hr {
	border: none;
	border-top: 1px solid rgba(148, 163, 184, 0.35);
	margin: 10px 0;
}

.chat-bubble-md ::v-deep .md-table-wrap {
	width: 100%;
	max-width: 100%;
	margin: 8px 0;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
	background: rgba(255, 255, 255, 0.55);
	border: 1px solid rgba(148, 163, 184, 0.35);
	border-radius: 8px;
}

/* barra de rolagem fininha — webkit */
.chat-bubble-md ::v-deep .md-table-wrap::-webkit-scrollbar {
	height: 6px;
}
.chat-bubble-md ::v-deep .md-table-wrap::-webkit-scrollbar-thumb {
	background: rgba(99, 102, 241, 0.4);
	border-radius: 3px;
}
.chat-bubble-md ::v-deep .md-table-wrap::-webkit-scrollbar-track {
	background: transparent;
}

.chat-bubble-md ::v-deep table {
	width: auto;
	min-width: 100%;
	border-collapse: collapse;
	margin: 0;
	font-size: 12.5px;
	background: transparent;
}

.chat-bubble-md ::v-deep thead {
	background: rgba(99, 102, 241, 0.12);
}

.chat-bubble-md ::v-deep th,
.chat-bubble-md ::v-deep td {
	padding: 8px 12px;
	text-align: left;
	border-bottom: 1px solid rgba(148, 163, 184, 0.25);
	vertical-align: top;
	white-space: nowrap;
}

/* Quando a célula tem muito texto, deixa ela quebrar mas com largura
   confortável — o ideal é o conteúdo ditar largura natural. */
.chat-bubble-md ::v-deep td.md-cell-wrap,
.chat-bubble-md ::v-deep th.md-cell-wrap {
	white-space: normal;
	min-width: 220px;
}

.chat-bubble-md ::v-deep th {
	font-weight: 700;
	color: #0f172a;
	position: sticky;
	top: 0;
}

.chat-bubble-md ::v-deep tr:last-child td {
	border-bottom: none;
}

.chat-bubble--user {
	border-bottom-right-radius: 4px;
	box-shadow: 0 12px 24px -16px rgba(15, 23, 42, 0.35);
}

.chat-bubble--assistant {
	background: rgba(255, 255, 255, 0.78);
	border: 1px solid rgba(148, 163, 184, 0.35);
	color: #111827;
	border-bottom-left-radius: 4px;
	backdrop-filter: blur(8px);
}

.chat-bubble--pending {
	padding: 12px 16px;
	min-width: 60px;
}

.chat-bubble-dots {
	display: inline-flex;
	gap: 4px;
	align-items: center;
	height: 14px;
}

.chat-bubble-dots i {
	width: 6px;
	height: 6px;
	background: #94a3b8;
	border-radius: 50%;
	display: inline-block;
	animation: dot-pulse 1.2s infinite ease-in-out;
}

.chat-bubble-dots i:nth-child(2) {
	animation-delay: 0.15s;
}
.chat-bubble-dots i:nth-child(3) {
	animation-delay: 0.3s;
}

@keyframes dot-pulse {
	0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
	30% { transform: translateY(-4px); opacity: 1; }
}

@keyframes bubble-in {
	from { opacity: 0; transform: translateY(6px); }
	to { opacity: 1; transform: translateY(0); }
}
</style>
